import { type ReactNode, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import qs from "qs";
import moment from "moment-timezone";
import { FilterContext, type FilterContextValue } from "./FilterContext";

// Функция для очистки значений фильтра
const cleanFilterValues = (obj: Record<string, any>): Record<string, any> => {
  const cleaned: Record<string, any> = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    // Пропускаем пустые строки, null, undefined
    if (value === "" || value === null || value === undefined) {
      return;
    }

    // Приводим строки "true"/"false" к булевым значениям
    if (value === "true") {
      cleaned[key] = true;
    } else if (value === "false") {
      cleaned[key] = false;
    } else if (Array.isArray(value)) {
      // Для массивов применяем очистку к каждому элементу
      const cleanedArray = value
        .map((item) => {
          if (item === "true") return true;
          if (item === "false") return false;
          if (item === "" || item === null || item === undefined) return null;
          return item;
        })
        .filter((item) => item !== null);

      if (cleanedArray.length > 0) {
        cleaned[key] = cleanedArray;
      }
    } else {
      cleaned[key] = value;
    }
  });

  return cleaned;
};

interface FilterProviderProps {
  children: ReactNode;
  initialPeriod?: "day" | "month" | "week" | "year";
}

export function FilterProvider({
  children,
  initialPeriod,
}: FilterProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const { filter, params } = useMemo(() => {
    // arrayLimit: у `qs` дефолт 20, и с 21-го элемента `filter[productIds][]`
    // приезжает не массивом, а объектом `{ "0": ..., "1": ... }`: фильтр по
    // продуктам молча уходил бы пустым, а `ProductsAutocomplete` падал бы на
    // `value.includes`. Партнёр с сотней продуктов выбирает их все руками,
    // поэтому лимит поднят с запасом.
    const { filter = {}, ...params } = qs.parse(searchParams.toString(), {
      ignoreQueryPrefix: true,
      arrayLimit: 1000,
    }) as { filter?: Record<string, any>; [key: string]: any };

    // Очищаем filter и params от пустых значений и приводим булевы
    const cleanedFilter = cleanFilterValues(filter || {});
    const cleanedParams = cleanFilterValues(params);

    // Без дефолта dateFrom/dateTo аналитические вкладки при первом заходе
    // (URL без ?filter[dateFrom]) шлют GraphQL-запрос с пустым filter — все
    // dateFrom/dateTo там non-null, запрос падает ("Field dateFrom of
    // required type DateTime! was not provided"). Тот же приём, что и в
    // референсе (partner/components/Filter/FilterProvider.js).
    if (initialPeriod) {
      const dateFromMoment = cleanedFilter.dateFrom
        ? moment(cleanedFilter.dateFrom)
        : null;
      const dateToMoment = cleanedFilter.dateTo
        ? moment(cleanedFilter.dateTo)
        : null;

      cleanedFilter.dateFrom = (
        dateFromMoment?.isValid()
          ? dateFromMoment
          : moment().subtract(1, initialPeriod).startOf("day")
      ).format();
      cleanedFilter.dateTo = (
        dateToMoment?.isValid() ? dateToMoment : moment().endOf("day")
      ).format();
    }

    return { filter: cleanedFilter, params: cleanedParams };
  }, [searchParams, initialPeriod]);

  const changeParams = (items: Record<string, any>) => {
    Object.keys(items).forEach((key) => {
      const value = items[key];

      if (key.startsWith("filter[")) {
        if (params.page) {
          // при изменении фильтра, нужно сбросить страницу на 0
          searchParams.delete("page");
        }
      }

      if (Array.isArray(value)) {
        searchParams.delete(key + "[]");
        value.forEach((v) => {
          searchParams.append(key + "[]", String(v));
        });
      } else if (value) {
        searchParams.set(key, String(value));
      } else {
        searchParams.delete(key);
      }
    });
  };

  /**
   * Изменение одного параметра
   */
  const onChangeParam = (key: string, value: any) => {
    changeParams({ [key]: value });
    setSearchParams(searchParams);
  };

  /**
   * Изменение нескольких параметров
   */
  const onChangeParams = (items: Record<string, any>) => {
    changeParams(items);
    setSearchParams(searchParams);
  };

  const value: FilterContextValue = {
    filter,
    params: {
      ...params,
      // тк часто фильтр используется в списках, добавим это сюда
      page: Number(params.page || 0),
      rowsPerPage: Number(
        params.rowsPerPage || localStorage.getItem("rowsPerPage") || 10
      ),
    },

    initialPeriod,

    onClearFilter() {
      setSearchParams({});
    },

    onChangeParam,
    onChangeParams,

    onChangeFilter(key: string, value: any) {
      const filterKey = `filter[${key}]`;
      onChangeParam(filterKey, value);
    },

    // тк часто фильтр используется в списках, добавим это сюда
    onPageChange(_event: any, newPage: number) {
      onChangeParam("page", newPage);
    },

    // тк часто фильтр используется в списках, добавим это сюда
    onRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>) {
      const rowsPerPage = event.target.value;
      localStorage.setItem("rowsPerPage", rowsPerPage);
      onChangeParam("rowsPerPage", rowsPerPage);
      onChangeParam("page", 0);
    },
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}
